// Plato recurrence expansion
// ---------------------------------------------------------------------------
// Plato's /appointment endpoint returns RECURRING appointments as a single
// "master" record carrying the series' ORIGINAL start date (e.g. 2026-03-23)
// plus a `recur` rule — NOT one record per occurrence. The scheduler sync used
// to write each master to that literal start date, so a recurring block/lunch/
// leave that actually falls on the day being synced (e.g. a Monday LUNCH on
// 2026-07-13) never appeared on that day. That is the "doctor schedule didn't
// go in" symptom.
//
// This module expands each master into the concrete occurrence dates that fall
// inside the sync range, so the sync can file each occurrence on the correct day.
//
// Observed Plato `recur` shapes:
//   weekly: {"days":["MO","TU","TH","FR"],"freq":2,"until":20261231,"interval":1,"ex":[20260623]}
//   daily : {"freq":3,"until":20260713,"interval":1}
// `until` and `ex[]` are YYYYMMDD integers. `interval` is every-N-weeks (freq 2)
// or every-N-days (freq 3). Unsupported freqs fall back to the literal date so
// we never silently drop an appointment we don't understand.

const WEEKDAY_CODE: Record<string, number> = {
  SU: 0, MO: 1, TU: 2, WE: 3, TH: 4, FR: 5, SA: 6,
};

const MS_PER_DAY = 86400000;

interface PlatoRecurRule {
  freq?: number;
  interval?: number;
  until?: number;
  days?: string[];
  ex?: number[];
}

interface ExpandableAppointment {
  id?: string;
  _id?: string;
  starttime: string; // "YYYY-MM-DD HH:MM:SS"
  endtime?: string;
  recur?: string | Record<string, unknown> | null;
  [key: string]: unknown;
}

const parseRecur = (recur: unknown): PlatoRecurRule | null => {
  if (!recur || recur === 'null') return null;
  if (typeof recur === 'string') {
    try {
      return JSON.parse(recur) as PlatoRecurRule;
    } catch {
      return null;
    }
  }
  if (typeof recur === 'object') return recur as PlatoRecurRule;
  return null;
};

// UTC-based date math so weekday/day-diff are independent of the host timezone.
const utcMs = (dateStr: string): number => {
  const [y, m, d] = dateStr.slice(0, 10).split('-').map(Number);
  return Date.UTC(y, m - 1, d);
};

const toDateStr = (ms: number): string => new Date(ms).toISOString().slice(0, 10);

const intToDateStr = (n: number): string => {
  const s = String(n);
  return `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}`;
};

const startOfWeekMs = (ms: number): number => {
  const weekday = new Date(ms).getUTCDay(); // 0 = Sunday
  return ms - weekday * MS_PER_DAY;
};

/**
 * Returns the concrete occurrence dates (YYYY-MM-DD) of a Plato appointment
 * that fall within [rangeStart, rangeEnd] (inclusive). For a non-recurring
 * appointment this is just its own date. For an unsupported recurrence rule we
 * fall back to the literal master date (previous behaviour) rather than drop it.
 */
export const getPlatoOccurrenceDates = (
  appt: Pick<ExpandableAppointment, 'starttime' | 'recur'>,
  rangeStart: string,
  rangeEnd: string,
): string[] => {
  const masterDate = appt.starttime.slice(0, 10);
  const rule = parseRecur(appt.recur);

  // Non-recurring: Plato only returned it because it lands in range, so keep it.
  if (!rule || !rule.freq) return [masterDate];

  // Only weekly (2) and daily (3) are understood; keep others as-is.
  if (rule.freq !== 2 && rule.freq !== 3) return [masterDate];

  const interval = rule.interval && rule.interval > 0 ? rule.interval : 1;
  const exceptions = new Set((rule.ex ?? []).map(intToDateStr));
  const dayCodes = new Set(
    (rule.days ?? [])
      .map(code => WEEKDAY_CODE[code])
      .filter((n): n is number => n !== undefined),
  );

  const masterMs = utcMs(masterDate);
  const untilMs = rule.until ? utcMs(intToDateStr(rule.until)) : Infinity;

  // Occurrences can only be on/after the master start and on/before `until`.
  const startMs = Math.max(utcMs(rangeStart), masterMs);
  const endMs = Math.min(utcMs(rangeEnd), untilMs);

  const out: string[] = [];
  for (let ms = startMs; ms <= endMs; ms += MS_PER_DAY) {
    const dateStr = toDateStr(ms);
    if (exceptions.has(dateStr)) continue;

    if (rule.freq === 2) {
      // Weekly on specific weekdays, every `interval` weeks.
      if (dayCodes.size > 0 && !dayCodes.has(new Date(ms).getUTCDay())) continue;
      const weeksApart = Math.round((startOfWeekMs(ms) - startOfWeekMs(masterMs)) / (7 * MS_PER_DAY));
      if (weeksApart % interval !== 0) continue;
      out.push(dateStr);
    } else {
      // Daily, every `interval` days.
      const daysApart = Math.round((ms - masterMs) / MS_PER_DAY);
      if (daysApart % interval !== 0) continue;
      out.push(dateStr);
    }
  }

  return out;
};

/**
 * Expands a list of Plato appointments so every recurring occurrence inside
 * [rangeStart, rangeEnd] becomes its own appointment dated to the correct day
 * (time-of-day preserved). Masters with no occurrence in range are dropped.
 *
 * When a single master yields more than one occurrence in the range (only
 * possible on a multi-day / week sync), each occurrence gets a per-date
 * `external_id` (`<masterId>::<date>`) so the sync can track them independently.
 * A single-occurrence expansion keeps the original id, so day-syncs stay fully
 * backward compatible with existing sync records.
 */
export const expandPlatoAppointmentsToRange = <T extends ExpandableAppointment>(
  appointments: T[],
  rangeStart: string,
  rangeEnd: string,
): T[] => {
  const expanded: T[] = [];

  for (const appt of appointments) {
    const timePart = appt.starttime.slice(11) || '00:00:00';
    const endTimePart = (appt.endtime ?? '').slice(11) || timePart;
    const occurrences = getPlatoOccurrenceDates(appt, rangeStart, rangeEnd);

    if (occurrences.length === 0) continue;

    if (occurrences.length === 1) {
      const date = occurrences[0];
      expanded.push({
        ...appt,
        starttime: `${date} ${timePart}`,
        endtime: `${date} ${endTimePart}`,
      });
      continue;
    }

    for (const date of occurrences) {
      const masterId = appt.id ?? appt._id ?? '';
      expanded.push({
        ...appt,
        id: `${masterId}::${date}`,
        starttime: `${date} ${timePart}`,
        endtime: `${date} ${endTimePart}`,
      });
    }
  }

  return expanded;
};
